import React, { useState } from 'react';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useEscortsData } from '../hooks/useEscortsData';
import { useTranslation } from '../hooks/useTranslation';

interface CityData {
  name: string;
  count: number;
}

interface CitySelectorProps {
  onCitySelect: (location: string) => void;
}

const CitySelector: React.FC<CitySelectorProps> = ({ onCitySelect }) => {
  const { escorts } = useEscortsData();
  const [showAllCities, setShowAllCities] = useState(false);
  const { t } = useTranslation();

  // Funzione per estrarre e contare le città dai dati API
  const getCitiesFromAPI = (): CityData[] => {
    const cityCount = new Map<string, number>();
    
    escorts.forEach(escort => {
      if (escort.location) {
        // Normalizza il nome della città (rimuovi spazi extra, capitalizza)
        const cityName = escort.location.trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        cityCount.set(cityName, (cityCount.get(cityName) || 0) + 1);
      }
    });
    
    // Converti in array e ordina per numero di annunci (decrescente)
    return Array.from(cityCount.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Città con più annunci (threshold: almeno 1 annuncio)
  const getTopCities = (): CityData[] => {
    return getCitiesFromAPI().filter(city => city.count >= 1);
  };

  // Città da mostrare in base allo stato
  const getCitiesToShow = (): CityData[] => {
    const topCities = getTopCities();
    if (showAllCities) {
      return topCities;
    }
    // Mostra le prime 12 città con più annunci
    return topCities.slice(0, 12);
  };

  const handleCitySelect = (cityName: string) => {
    onCitySelect(cityName);
  };

  return (
    <>
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            {t('citySelector.title')}
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            {t('citySelector.subtitle')}
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {getCitiesToShow().map((city) => (
              <button
                key={city.name}
                onClick={() => handleCitySelect(city.name)}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300 text-center group hover:bg-pink-50"
                aria-label={`${city.name} - ${city.count} ${city.count === 1 ? t('citySelector.singleAd') : t('citySelector.multipleAds')}`}
              >
                <MapPin className="h-6 w-6 text-pink-500 mx-auto mb-2 group-hover:text-pink-600" />
                <h3 className="font-semibold text-gray-800 group-hover:text-pink-600 text-sm">
                  {city.name}
                </h3>
                <span className="inline-block mt-1 bg-pink-100 text-pink-700 text-xs px-2 py-1 rounded-full">
                  {city.count} {city.count === 1 ? t('citySelector.singleAd') : t('citySelector.multipleAds')}
                </span>
              </button>
            ))}
          </div>
          
          {/* Bottone per mostrare/nascondere tutte le città */}
          {getTopCities().length > 12 && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllCities(!showAllCities)}
                className="inline-flex items-center px-6 py-3 bg-white border border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 hover:text-pink-600 hover:border-pink-300"
                aria-label={showAllCities ? t('citySelector.showMainCities') : t('citySelector.showAllCities')}
              >
                {showAllCities ? (
                  <>
                    <ChevronUp className="h-5 w-5 mr-2" />
                    {t('citySelector.showMainCities')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-5 w-5 mr-2" />
                    {t('citySelector.showAllCities')} ({getTopCities().length})
                  </>
                )}
              </button>
            </div>
          )}
          
          {getCitiesToShow().length === 0 && (
            <div className="text-center py-8">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('citySelector.noCitiesAvailable')}</p>
              <p className="text-gray-400 text-sm mt-2">{t('citySelector.dataLoadingMessage')}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CitySelector;
